import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../store';
import {
  Glossary,
  GlossaryContent,
  GlossaryID,
  IGlossariesState,
} from './glossaries.types';

export const initialState: IGlossariesState = {
  glossaries: {},
  adminGlossaries: {},
  activeGlossaryID: null,
  activeAdminGlossaryID: null,
};

export const glossariesSlice = createSlice({
  name: 'glossaries',
  initialState,
  reducers: {
    // USERS
    setGlossaries(state, { payload }: PayloadAction<Glossary[]>) {
      if (payload) {
        const glossaries: Record<GlossaryID, Glossary> = {};
        payload.map((g) => {
          glossaries[g.id] = {
            id: g.id,
            name: g.name,
            description: g.description,
            date: g.date,
            source: g.source,
            target: g.target,
            content: g.content,
          };
        });
        state.glossaries = glossaries;
      } else {
        state.glossaries = {};
      }
    },
    setGlossaryContent(
      state,
      {
        payload: { glossaryId, content },
      }: PayloadAction<
        { glossaryId: GlossaryID } & { content: GlossaryContent }
      >,
    ) {
      state.glossaries[glossaryId].content = content;
    },
    updateGlossary(state, { payload }: PayloadAction<Glossary>) {
      state.glossaries[payload.id] = payload;
    },
    addGlossary(state, { payload }: PayloadAction<Glossary>) {
      if (payload) {
        state.glossaries[payload.id] = payload;
      }
    },
    deleteGlossary(state, { payload }: PayloadAction<GlossaryID>) {
      delete state.glossaries[payload];
    },
    setActiveGlossaryID(state, { payload }: PayloadAction<GlossaryID | null>) {
      state.activeGlossaryID = payload;
    },
    // ADMIN
    setAdminGlossaries(state, { payload }: PayloadAction<Glossary[]>) {
      if (payload) {
        const adminGlossaries: Record<GlossaryID, Glossary> = {};
        payload.map((g) => {
          adminGlossaries[g.id] = {
            id: g.id,
            name: g.name,
            description: g.description,
            date: g.date,
            source: g.source,
            target: g.target,
            content: g.content,
          };
        });
        state.adminGlossaries = adminGlossaries;
      } else {
        state.adminGlossaries = {};
      }
    },
    setAdminGlossaryContent(
      state,
      {
        payload: { glossaryId, content },
      }: PayloadAction<
        { glossaryId: GlossaryID } & { content: GlossaryContent }
      >,
    ) {
      state.adminGlossaries[glossaryId].content = content;
    },
    updateAdminGlossary(state, { payload }: PayloadAction<Glossary>) {
      state.adminGlossaries[payload.id] = payload;
    },
    addAdminGlossary(state, { payload }: PayloadAction<Glossary>) {
      if (payload) {
        state.adminGlossaries[payload.id] = payload;
      }
    },
    deleteAdminGlossary(state, { payload }: PayloadAction<GlossaryID>) {
      delete state.adminGlossaries[payload];
    },
    setActiveAdminGlossaryID(
      state,
      { payload }: PayloadAction<GlossaryID | null>,
    ) {
      state.activeAdminGlossaryID = payload;
    },
  },
});

export const {
  updateGlossary,
  setGlossaries,
  setGlossaryContent,
  addGlossary,
  deleteGlossary,
  updateAdminGlossary,
  setAdminGlossaries,
  setAdminGlossaryContent,
  addAdminGlossary,
  deleteAdminGlossary,
  setActiveGlossaryID,
  setActiveAdminGlossaryID,
} = glossariesSlice.actions;

export const glossarySelector = (state: RootState): IGlossariesState =>
  state.glossaries;

export const selectGlossaries = createSelector(
  glossarySelector,
  (glossaries) => glossaries.glossaries,
);
export const selectAdminGlossaries = createSelector(
  glossarySelector,
  (glossaries) => glossaries.adminGlossaries,
);
export const selectActiveGlossaryID = createSelector(
  glossarySelector,
  (glossaries) => glossaries.activeGlossaryID,
);
export const selectActiveAdminGlossaryID = createSelector(
  glossarySelector,
  (glossaries) => glossaries.activeAdminGlossaryID,
);
